import React from 'react';
import LinkButton from '|/components/carvana-ds/link-button';
import VehicleImage from '|/components/vehicle-image';
import styles from './slate-mobile-vehicle-card.module.css';
import type { VehicleSummary } from '../vehicle-card/types';
import {
  financeTotalLabel,
  formatCurrency,
  getNonFinanceLabel
} from '../vehicle-card/vehicle-card-helpers';

/**
 * The stat labels are written as column headings ("Total due") but here they trail the
 * amount mid-sentence. Only the first character is lowered — `toLowerCase()` on the whole
 * string would also de-capitalize "Carvana" in the self-arranged label.
 */
const toInlineLabel = (label: string) => label.charAt(0).toLowerCase() + label.slice(1);

interface SlateMobileVehicleCardProps {
  summary: VehicleSummary;
  onViewOrderDetails?: () => void;
  onEditOrder?: () => void;
}

const SlateMobileVehicleCard: React.FC<SlateMobileVehicleCardProps> = ({
  summary,
  onViewOrderDetails,
  onEditOrder
}) => {
  const { stockNumber, year, make, model, purchaseType, monthlyPayment, cashDue } = summary;

  const isFinance = purchaseType === 'finance';
  const totalDisplay = isFinance ? financeTotalLabel(cashDue ?? 0) : getNonFinanceLabel(summary);

  return (
    <div className={styles.card} data-testid="pp_checkout_slate-mobile-vehicle-card">
      <div className={styles.summary}>
        <div className={styles.mediaWrap}>
          <VehicleImage
            stockNumber={stockNumber}
            width={172}
            alt={`${year} ${make} ${model}`}
            className={styles.media}
          />
        </div>

        <div className={styles.details}>
          <p className={styles.vehicleName}>
            {year} {make} {model}
          </p>

          <div className={styles.pricing}>
            {isFinance && (
              <>
                <span className={styles.stat}>
                  <span className={styles.statAmount}>{formatCurrency(monthlyPayment ?? 0)}</span>
                  <span className={styles.statLabel}>/mo</span>
                </span>
                <span className={styles.pricingDivider} aria-hidden>
                  <span className={styles.pricingDividerDot} />
                </span>
              </>
            )}
            <span className={styles.stat}>
              <span className={styles.statAmount}>{formatCurrency(totalDisplay.amount)}</span>
              <span className={styles.statLabel}>{toInlineLabel(totalDisplay.label)}</span>
            </span>
          </div>

          <div className={styles.links}>
            {onEditOrder && (
              <LinkButton
                size="sm"
                onClick={onEditOrder}
                data-analytics-id="pp_checkout_edit_order"
                data-testid="pp_checkout_slate-mobile-vehicle-card_edit-order"
              >
                Edit order
              </LinkButton>
            )}
            <LinkButton
              size="sm"
              onClick={onViewOrderDetails}
              data-analytics-id="checkout-mobile-vehicle-card-review-order"
              data-testid="pp_checkout_slate-mobile-vehicle-card_price-details"
            >
              Price details
            </LinkButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlateMobileVehicleCard;
